import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Globe, ChevronDown, ChevronUp, Save, Loader2, AlertTriangle,
  Plus, Trash2, GripVertical, Edit
} from 'lucide-react';

export default function AdminSiteSettingsPage() {
  const { api } = useAuth();
  const [navConfig, setNavConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [addDialog, setAddDialog] = useState(null); // null, 'menu', or dropdown key
  const [editDialog, setEditDialog] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [newItem, setNewItem] = useState({ label: '', href: '', description: '', type: 'link', filterSubcategory: '', filterGradeBand: '' });

  // Map dropdown keys to their parent page href
  const getParentHref = (key) => {
    const parent = (navConfig?.items || []).find(i => i.dropdownId === key);
    return parent?.href || `/${key}`;
  };

  // STEM division keys that support catalog filters
  const catalogDropdownKeys = ['science', 'technology', 'engineering', 'math', 'summer-camps'];

  // Build href from parent page + filters
  const buildFilterHref = (parentHref, subcategory, gradeBand) => {
    const params = new URLSearchParams();
    if (subcategory) params.set('subcategory', subcategory);
    if (gradeBand) params.set('gradeBand', gradeBand);
    const query = params.toString();
    return query ? `${parentHref}?${query}#catalog` : parentHref;
  };

  useEffect(() => { fetchNavConfig(); fetchSubcategories(); }, []);

  const fetchNavConfig = async () => {
    try {
      const res = await api.get('/site-settings/nav/');
      setNavConfig(res.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const fetchSubcategories = async () => {
    try {
      const res = await api.get('/courses/');
      const courses = res.data?.results || res.data || [];
      const subs = [...new Set(courses.map(c => c.subcategory).filter(Boolean))].sort();
      setSubcategories(subs);
    } catch { /* silently fail — subcategories just won't populate */ }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await api.put('/site-settings/nav/', navConfig);
      toast.success('Navigation saved!');
      setHasChanges(false);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const modify = (fn) => { fn(); setHasChanges(true); };

  // Reorder
  const moveItem = (arr, index, direction) => {
    const newArr = [...arr];
    const target = index + direction;
    if (target < 0 || target >= newArr.length) return arr;
    [newArr[index], newArr[target]] = [newArr[target], newArr[index]];
    return newArr;
  };

  const moveMainItem = (index, dir) => modify(() => {
    setNavConfig(prev => ({ ...prev, items: moveItem(prev.items, index, dir) }));
  });

  const moveSubItem = (key, index, dir) => modify(() => {
    setNavConfig(prev => ({
      ...prev, dropdown_items: { ...prev.dropdown_items, [key]: moveItem(prev.dropdown_items[key], index, dir) }
    }));
  });

  // Toggle
  const toggleMain = (index) => modify(() => {
    setNavConfig(prev => {
      const items = [...prev.items];
      items[index] = { ...items[index], visible: !items[index].visible };
      return { ...prev, items };
    });
  });

  const toggleSub = (key, index) => modify(() => {
    setNavConfig(prev => {
      const subs = [...(prev.dropdown_items[key] || [])];
      subs[index] = { ...subs[index], visible: !subs[index].visible };
      return { ...prev, dropdown_items: { ...prev.dropdown_items, [key]: subs } };
    });
  });

  // Delete
  const deleteMain = (index) => {
    if (!window.confirm('Remove this menu item?')) return;
    modify(() => {
      setNavConfig(prev => {
        const item = prev.items[index];
        const items = prev.items.filter((_, i) => i !== index);
        const dropdown_items = { ...prev.dropdown_items };
        if (item.dropdownId) delete dropdown_items[item.dropdownId];
        return { ...prev, items, dropdown_items };
      });
    });
  };

  const deleteSub = (key, index) => modify(() => {
    setNavConfig(prev => ({
      ...prev, dropdown_items: { ...prev.dropdown_items, [key]: prev.dropdown_items[key].filter((_, i) => i !== index) }
    }));
  });

  // Add
  const addMenuItem = () => {
    if (!newItem.label || !newItem.href) { toast.error('Label and link required'); return; }
    const id = newItem.label.toLowerCase().replace(/\s+/g, '-');
    const hasDropdown = newItem.type === 'dropdown';
    modify(() => {
      setNavConfig(prev => ({
        ...prev,
        items: [...prev.items, { id, label: newItem.label, href: newItem.href, type: newItem.type, visible: true, hasDropdown, dropdownId: hasDropdown ? id : undefined }],
        dropdown_items: hasDropdown ? { ...prev.dropdown_items, [id]: [] } : prev.dropdown_items,
      }));
    });
    setAddDialog(null);
    setNewItem({ label: '', href: '', description: '', type: 'link', filterSubcategory: '', filterGradeBand: '' });
  };

  const addSubItem = (key) => {
    if (!newItem.label) { toast.error('Label required'); return; }
    const id = `${key}-${newItem.label.toLowerCase().replace(/\s+/g, '-')}`;
    const isCatalogDropdown = catalogDropdownKeys.includes(key);
    const href = isCatalogDropdown 
      ? buildFilterHref(getParentHref(key), newItem.filterSubcategory, newItem.filterGradeBand)
      : newItem.href;
    if (!href) { toast.error('Link required'); return; }
    const filters = (newItem.filterSubcategory || newItem.filterGradeBand) 
      ? { subcategory: newItem.filterSubcategory || '', gradeBand: newItem.filterGradeBand || '' } 
      : undefined;
    modify(() => {
      setNavConfig(prev => ({
        ...prev, dropdown_items: { ...prev.dropdown_items, [key]: [...(prev.dropdown_items[key] || []), { id, label: newItem.label, href, description: newItem.description, visible: true, filters }] }
      }));
    });
    setAddDialog(null);
    setNewItem({ label: '', href: '', description: '', type: 'link', filterSubcategory: '', filterGradeBand: '' });
  };

  // Edit
  const saveEdit = () => {
    if (!editDialog) return;
    const { section, index, data } = editDialog;
    modify(() => {
      if (section === 'items') {
        setNavConfig(prev => {
          const items = [...prev.items];
          items[index] = { ...items[index], ...data };
          return { ...prev, items };
        });
      } else {
        const key = section;
        const isCatalogDropdown = catalogDropdownKeys.includes(key);
        setNavConfig(prev => {
          const subs = [...(prev.dropdown_items[key] || [])];
          const updatedData = { ...data };
          if (isCatalogDropdown && (data.filterSubcategory !== undefined || data.filterGradeBand !== undefined)) {
            updatedData.href = buildFilterHref(getParentHref(key), data.filterSubcategory || '', data.filterGradeBand || '');
            updatedData.filters = (data.filterSubcategory || data.filterGradeBand) 
              ? { subcategory: data.filterSubcategory || '', gradeBand: data.filterGradeBand || '' }
              : undefined;
          }
          delete updatedData.filterSubcategory;
          delete updatedData.filterGradeBand;
          subs[index] = { ...subs[index], ...updatedData };
          return { ...prev, dropdown_items: { ...prev.dropdown_items, [key]: subs } };
        });
      }
    });
    setEditDialog(null);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      {/* Save bar */}
      {hasChanges && (
        <div className="sticky top-0 z-20 bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-center justify-between shadow-md">
          <div className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /><span className="font-semibold text-amber-900">Unsaved changes</span></div>
          <Button onClick={saveAll} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save Navigation</Button>
        </div>
      )}

      {/* Main Nav */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2"><Globe className="w-5 h-5" /> Main Navigation Bar</CardTitle>
              <CardDescription>Drag to reorder. Click ✏️ to edit. Toggle to show/hide.</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => { setNewItem({ label: '', href: '', description: '', type: 'link', filterSubcategory: '', filterGradeBand: '' }); setAddDialog('menu'); }}>
              <Plus className="h-4 w-4 mr-1" /> Add Menu Item
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {(navConfig?.items || []).map((item, i) => (
            <div key={item.id} className={`flex items-center gap-2 p-3 rounded-lg border ${item.visible ? 'bg-white' : 'bg-gray-50 opacity-50'}`}>
              <div className="flex flex-col gap-0.5">
                <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => moveMainItem(i, -1)} disabled={i === 0}><ChevronUp className="h-3 w-3" /></Button>
                <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => moveMainItem(i, 1)} disabled={i === navConfig.items.length - 1}><ChevronDown className="h-3 w-3" /></Button>
              </div>
              <div className={`w-2 h-2 rounded-full ${item.visible ? 'bg-green-500' : 'bg-gray-300'}`} />
              <div className="flex-1">
                <span className="font-semibold">{item.label}</span>
                <span className="text-xs text-muted-foreground ml-2">{item.href}</span>
                {item.hasDropdown && <Badge variant="outline" className="ml-2 text-[10px]">has dropdown</Badge>}
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditDialog({ section: 'items', index: i, data: { label: item.label, href: item.href } })}><Edit className="h-3 w-3" /></Button>
              <Switch checked={item.visible} onCheckedChange={() => toggleMain(i)} />
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteMain(i)} disabled={item.id === 'home'}><Trash2 className="h-3 w-3 text-red-500" /></Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Dropdown Sections */}
      {navConfig?.dropdown_items && Object.entries(navConfig.dropdown_items).map(([key, items]) => {
        const parent = (navConfig?.items || []).find(i => i.dropdownId === key);
        return (
          <Card key={key}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2"><ChevronDown className="w-5 h-5" /> "{parent?.label || key}" Submenu</CardTitle>
                  <CardDescription>Items in the {parent?.label || key} dropdown.</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => { setNewItem({ label: '', href: '', description: '', type: 'link', filterSubcategory: '', filterGradeBand: '' }); setAddDialog(key); }}>
                  <Plus className="h-4 w-4 mr-1" /> Add Link
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {(items || []).map((item, i) => (
                <div key={item.id} className={`flex items-center gap-2 p-2 rounded-lg border ${item.visible ? 'bg-white' : 'bg-gray-50 opacity-50'}`}>
                  <div className="flex flex-col gap-0.5">
                    <Button variant="ghost" size="icon" className="h-4 w-4" onClick={() => moveSubItem(key, i, -1)} disabled={i === 0}><ChevronUp className="h-2.5 w-2.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-4 w-4" onClick={() => moveSubItem(key, i, 1)} disabled={i === items.length - 1}><ChevronDown className="h-2.5 w-2.5" /></Button>
                  </div>
                  <div className={`w-2 h-2 rounded-full ${item.visible ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <div className="flex-1">
                    <span className="font-medium text-sm">{item.label}</span>
                    <span className="text-xs text-muted-foreground ml-2">{item.href}</span>
                    {item.description && <span className="text-xs text-muted-foreground ml-1">· {item.description}</span>}
                    {item.filters?.subcategory && <Badge variant="secondary" className="ml-2 text-[10px]">subcategory: {item.filters.subcategory}</Badge>}
                    {item.filters?.gradeBand && <Badge variant="secondary" className="ml-2 text-[10px]">grade: {item.filters.gradeBand}</Badge>}
                  </div>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setEditDialog({ section: key, index: i, data: { label: item.label, href: item.href, description: item.description || '', filterSubcategory: item.filters?.subcategory || '', filterGradeBand: item.filters?.gradeBand || '' } })}><Edit className="h-3 w-3" /></Button>
                  <Switch checked={item.visible} onCheckedChange={() => toggleSub(key, i)} />
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => deleteSub(key, i)}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                </div>
              ))}
              {(!items || items.length === 0) && <p className="text-sm text-muted-foreground text-center py-3">No submenu items yet</p>}
            </CardContent>
          </Card>
        );
      })}

      {/* Save button */}
      <div className="flex justify-end">
        <Button onClick={saveAll} disabled={saving || !hasChanges} size="lg">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Navigation
        </Button>
      </div>

      {/* ADD DIALOG */}
      <Dialog open={!!addDialog} onOpenChange={() => setAddDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{addDialog === 'menu' ? 'Add Menu Item' : `Add Link to "${(navConfig?.items || []).find(i => i.dropdownId === addDialog)?.label || addDialog}" Submenu`}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Label *</Label><Input value={newItem.label} onChange={e => setNewItem(p => ({...p, label: e.target.value}))} placeholder="e.g., Python Classes" /></div>
            {addDialog !== 'menu' && catalogDropdownKeys.includes(addDialog) ? (
              <>
                <div className="space-y-2"><Label>Description (optional)</Label><Input value={newItem.description} onChange={e => setNewItem(p => ({...p, description: e.target.value}))} placeholder="Short description for dropdown" /></div>
                <div className="p-3 bg-blue-50 rounded-lg space-y-3">
                  <p className="text-sm font-semibold text-blue-800">Catalog Filters</p>
                  <p className="text-xs text-blue-600">Configure which courses appear when visitors click this link. The link will anchor to the embedded catalog on the {(navConfig?.items || []).find(i => i.dropdownId === addDialog)?.label} page.</p>
                  <div className="space-y-2">
                    <Label className="text-sm">Subcategory Filter <span className="text-muted-foreground font-normal">(optional)</span></Label>
                    <Select value={newItem.filterSubcategory || "none"} onValueChange={v => setNewItem(p => ({...p, filterSubcategory: v === "none" ? "" : v}))}>
                      <SelectTrigger data-testid="add-filter-subcategory"><SelectValue placeholder="All Subcategories" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">All Subcategories</SelectItem>
                        {subcategories.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Grade Band Filter <span className="text-muted-foreground font-normal">(optional)</span></Label>
                    <Select value={newItem.filterGradeBand || "none"} onValueChange={v => setNewItem(p => ({...p, filterGradeBand: v === "none" ? "" : v}))}>
                      <SelectTrigger data-testid="add-filter-grade"><SelectValue placeholder="All Grades" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">All Grades</SelectItem>
                        <SelectItem value="K-2">K-2</SelectItem>
                        <SelectItem value="3-5">3-5</SelectItem>
                        <SelectItem value="6-8">6-8</SelectItem>
                        <SelectItem value="9-12">9-12</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {(newItem.filterSubcategory || newItem.filterGradeBand) && (
                    <p className="text-xs text-blue-700 font-mono bg-blue-100 p-2 rounded">Preview: {buildFilterHref(getParentHref(addDialog), newItem.filterSubcategory, newItem.filterGradeBand)}</p>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2"><Label>Link *</Label><Input value={newItem.href} onChange={e => setNewItem(p => ({...p, href: e.target.value}))} placeholder="e.g., /summer-camps" /></div>
                {addDialog !== 'menu' && (
                  <div className="space-y-2"><Label>Description (optional)</Label><Input value={newItem.description} onChange={e => setNewItem(p => ({...p, description: e.target.value}))} placeholder="Short description for dropdown" /></div>
                )}
                {addDialog === 'menu' && (
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={newItem.type} onValueChange={v => setNewItem(p => ({...p, type: v}))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="link">Simple Link (no dropdown)</SelectItem>
                        <SelectItem value="dropdown">Dropdown Menu (with submenu)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog(null)}>Cancel</Button>
            <Button onClick={() => addDialog === 'menu' ? addMenuItem() : addSubItem(addDialog)}>
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT DIALOG */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Item</DialogTitle></DialogHeader>
          {editDialog && (
            <div className="space-y-4">
              <div className="space-y-2"><Label>Label</Label><Input value={editDialog.data.label} onChange={e => setEditDialog(p => ({...p, data: {...p.data, label: e.target.value}}))} /></div>
              {editDialog.section === 'items' || !catalogDropdownKeys.includes(editDialog.section) ? (
                <>
                  <div className="space-y-2"><Label>Link</Label><Input value={editDialog.data.href} onChange={e => setEditDialog(p => ({...p, data: {...p.data, href: e.target.value}}))} /></div>
                  {editDialog.section !== 'items' && (
                    <div className="space-y-2"><Label>Description</Label><Input value={editDialog.data.description || ''} onChange={e => setEditDialog(p => ({...p, data: {...p.data, description: e.target.value}}))} /></div>
                  )}
                </>
              ) : (
                <>
                  <div className="space-y-2"><Label>Description</Label><Input value={editDialog.data.description || ''} onChange={e => setEditDialog(p => ({...p, data: {...p.data, description: e.target.value}}))} /></div>
                  <div className="p-3 bg-blue-50 rounded-lg space-y-3">
                    <p className="text-sm font-semibold text-blue-800">Catalog Filters</p>
                    <div className="space-y-2">
                      <Label className="text-sm">Subcategory Filter</Label>
                      <Select value={editDialog.data.filterSubcategory || "none"} onValueChange={v => setEditDialog(p => ({...p, data: {...p.data, filterSubcategory: v === "none" ? "" : v}}))}>
                        <SelectTrigger data-testid="edit-filter-subcategory"><SelectValue placeholder="All Subcategories" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">All Subcategories</SelectItem>
                          {subcategories.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Grade Band Filter</Label>
                      <Select value={editDialog.data.filterGradeBand || "none"} onValueChange={v => setEditDialog(p => ({...p, data: {...p.data, filterGradeBand: v === "none" ? "" : v}}))}>
                        <SelectTrigger data-testid="edit-filter-grade"><SelectValue placeholder="All Grades" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">All Grades</SelectItem>
                          <SelectItem value="K-2">K-2</SelectItem>
                          <SelectItem value="3-5">3-5</SelectItem>
                          <SelectItem value="6-8">6-8</SelectItem>
                          <SelectItem value="9-12">9-12</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="text-xs text-blue-700 font-mono bg-blue-100 p-2 rounded">Preview: {buildFilterHref(getParentHref(editDialog.section), editDialog.data.filterSubcategory || '', editDialog.data.filterGradeBand || '')}</p>
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)}>Cancel</Button>
            <Button onClick={saveEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
